import React from 'react';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import { useAppState } from '../../../../store/appstate';
import FooterButtonContainer from '../../../atoms/Modal/FooterButtonContainer';
import HeaderTitle from '../../../atoms/Modal/HeaderTitle';
import ModalContainer from '../../../atoms/Modal/ModalContainer';
import Spinner from '../../../atoms/Spinner/Spinner';
import { bulkFailedWebhook } from '../../../../api/webhooks/webhooks';
import ErrorMessage from '../../../atoms/Message/Error/ErrorMessage';
import { ErrorType } from '../../../../types/response/ErrorCodes';
import SuccessMessage from '../../../atoms/Message/Success/SuccessMessage';
import Button from '../../../atoms/Button/Button';
import { deadletterIds } from '../../../../types/webhooks/FailedWebhook';
import { SessionState } from '../../../../store/session/sessionSlice';
import { useWebhooksStore } from '../../../../store/admin/webhooks/webhooksStore';

interface BulkFailedWebhookModalProps {
    hide: () => void;
    theme: DefaultTheme;
}

const BulkFailedWebhookModal = ({ hide, theme }: BulkFailedWebhookModalProps) => {
    const {
        bulkSucceeded,
        bulkError,
        bulkFailedWebhookProcessing,
        bulkAction: { action, failedWebhooks: items },
        bulkFailedWebhookStarted,
        bulkFailedWebhookSucceeded,
        bulkFailedWebhookFailed,
        searchFailedWebhooksStarted,
        searchFailedWebhooksSucceeded,
        searchFailedWebhooksFailed,
        setSelectedFailedWebhooks,
    } = useWebhooksStore();
    const { selectedOrganization } = useAppState<SessionState>(s => s.session);

    const onConfirm = (bulkAction: string) => {
        const uniqueWebhookIds = [...new Set(items.map(x => x.action.webhookId))];
        const itemsToSend = [] as deadletterIds[];

        uniqueWebhookIds.forEach(webhookId => {
            const deadletterIds = items
                .filter(item => item.action.webhookId === webhookId)
                .map(item => item.id);
            itemsToSend.push({ webhookId, deadletterIds });
        });

        bulkFailedWebhook(
            itemsToSend,
            selectedOrganization.id,
            bulkAction,
            bulkFailedWebhookStarted,
            bulkFailedWebhookSucceeded,
            bulkFailedWebhookFailed,
            searchFailedWebhooksStarted,
            searchFailedWebhooksSucceeded,
            searchFailedWebhooksFailed
        );
    };

    const onClose = () => {
        hide();
        setSelectedFailedWebhooks([]);
    };

    const isPlural = () => {
        if (items.length > 1) return 's';
    };

    return (
        <>
            <ModalContainer position="header">
                <HeaderTitle>
                    {action} selected {action === 'Delete' ? 'failed webhook' : 'webhook'}
                    {isPlural()}
                </HeaderTitle>
            </ModalContainer>

            {!bulkSucceeded && !bulkError && (
                <ModalContainer position="content">
                    <Container>
                        <FailedWebhookProcessText>
                            <TitleText>{items.length}</TitleText>
                            failed webhook{isPlural()} will be processed.
                        </FailedWebhookProcessText>
                    </Container>
                </ModalContainer>
            )}

            {bulkSucceeded && (
                <ModalContainer position="content">
                    <ResultContainer>
                        <SuccessMessage>
                            The selected failed webhooks have been processed!
                        </SuccessMessage>
                    </ResultContainer>
                </ModalContainer>
            )}

            {bulkError && (
                <ModalContainer key={bulkError.title} position="error">
                    <ErrorMessage
                        error={bulkError}
                        errorHeader={
                            action === 'Resend' ? ErrorType.ResendWebhook : ErrorType.DeleteWebhook
                        }
                    />
                </ModalContainer>
            )}

            <ModalContainer position="footer">
                <FooterButtonContainer>
                    {!bulkSucceeded && !bulkError && (
                        <Button data-testid="bulk-modal-cancel-button" onClick={hide} large>
                            Cancel
                        </Button>
                    )}
                    {!bulkFailedWebhookProcessing && !bulkSucceeded && !bulkError && (
                        <Button
                            data-testid="bulk-modal-confirm-button"
                            tabIndex={0}
                            onClick={() => onConfirm(action)}
                            primary
                            large
                            disabled={items.length === 0}
                        >
                            Confirm
                        </Button>
                    )}
                    {bulkFailedWebhookProcessing && (
                        <Button
                            data-testid="bulk-modal-loading-button"
                            tabIndex={0}
                            primary
                            large
                            disabled
                        >
                            <Spinner color={theme.colors.light} size={8} loading />
                        </Button>
                    )}
                    {(bulkSucceeded || bulkError) && (
                        <Button
                            data-testid="bulk-modal-close-button"
                            tabIndex={0}
                            onClick={onClose}
                            primary
                            large
                        >
                            Close
                        </Button>
                    )}
                </FooterButtonContainer>
            </ModalContainer>
        </>
    );
};

export default withTheme(BulkFailedWebhookModal) as React.ComponentType<
    Omit<BulkFailedWebhookModalProps, 'theme'>
>;

const Container = styled.div`
    margin-bottom: ${props => props.theme.layout.margin.xLarge};
`;

const TitleText = styled.div`
    font-family: ${props => props.theme.text.font.bold};
    margin-right: ${props => props.theme.layout.margin.xxsmall};
`;

const FailedWebhookProcessText = styled.div`
    display: flex;
`;

const ResultContainer = styled.div`
    display: flex;
    flex-flow: row;
    justify-content: center;
    padding-bottom: 2.5rem;
`;
